import React, { FC, useEffect, useState } from 'react';
import { LayoutBox } from 'src/common/components/LayoutBox';
import { useResetRedirectUrl } from 'src/common/guards/useResetRedirectUrl';
import styled, { css } from 'styled-components';
import { colors } from 'src/config/colors';
import { below, media, widthBreakpoints } from 'src/config/breakpoints';
import { useMediaQuery } from 'src/common/migration/useMediaQuery';
import { Layout } from 'src/common/components/Layout';
import { SpeakerCard } from 'src/common/components/SpeakerCard';
import { SpeakerListingLeftPanel } from 'src/public/components/speaker-listing/SpeakerListingLeftPanel';
import { ListingItem, ListingResults } from 'src/common/components/ListingItems';
import { typography } from 'src/config/typography';
import { SpeakerListingYoutubeCard } from 'src/public/components/speaker-listing/SpeakerListingYoutubeCard';
import { SpeakerListingYoutubeStickyButton } from 'src/public/components/speaker-listing/SpeakerListingYoutubeStickyButton';
import { Loader } from 'src/common/components/Loader';
import { Speaker } from 'src/common/models/speaker';
import { flatten, isEmpty, range } from 'ramda';
import { getSpeakers, listSpeakersPageSize } from 'src/common/services/speakers';
import { useIntersectionObserver } from '@researchgate/react-intersection-observer';
import { SpeakerCardSkeleton } from 'src/common/components/SpeakerCardSkeleton';
import { useSpeakersQuery } from 'src/common/hooks/speaker/useSpeakersQuery';
import { VendorGetStaticProps } from 'vendor/utils/VendorGetStaticProps';
import { PageProps } from 'src/common/migration/PageProps';
import { PageResultDTO } from 'src/common/models/page';
import { useClientReady } from 'src/lib/SSR/ClientOnly/ClientOnly';

const Description = styled.div`
  font-family: ${typography.fontFamilyHeaders};
  font-size: 0.75rem;
  line-height: 1rem;
  color: ${colors.blue};
  margin-top: 0.3125rem;
  ${media.w.min.px576(css`
    font-size: 1rem;
    line-height: 1.375rem;
    margin-top: 0.625rem;
  `)}

  ${media.w.max.px992(css`
    max-width: 35.1875rem;
  `)}
`;

const StyledListingResults = styled(ListingResults)`
  ${media.w.min.px992(css`
    padding: 0 0 3.75rem 0;
  `)};
`;

const Card = styled.div`
  width: 13.25rem;

  ${media.w.min.px992(css`
    width: auto;
  `)};
`;

const ListingHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0.8125rem 0 1.5rem 0;

  ${media.w.min.px768(css`
    margin: 0 0 2.5rem 0;
    padding: 0 1.5rem;
  `)};

  ${media.w.min.px992(css`
    margin: 4.375rem 0 2.5rem 0;
    padding: 0;
  `)};
`;

const ListingTitle = styled.div`
  color: ${colors.red};
  font-family: ${typography.fontFamilyHeaders};
  font-size: 1rem;
  line-height: 1.375rem;

  ${media.w.min.px576(css`
    font-size: 1.5rem;
    line-height: 1.875rem;
  `)};

  ${media.w.min.px992(css`
    padding-left: 0;
  `)};

  span {
    color: ${colors.blue};
    font-size: 0.75rem;

    ${media.w.min.px576(css`
      font-size: 1rem;
    `)};
  }
`;

const blank = false;

type SpeakersPageProps = {
  speakersPage: PageResultDTO<Speaker>;
};

export const SpeakersPage: FC<PageProps<SpeakersPageProps>> = ({ speakersPage }) => {
  useResetRedirectUrl();
  const [speakersToShow, setSpeakerToShow] = useState<Speaker[]>([]);
  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = useSpeakersQuery(
    () => speakersPage,
  );

  const [moreEventsTriggerRef] = useIntersectionObserver(
    (entry) => {
      entry.isIntersecting && fetchNextPage();
    },
    {
      threshold: 1,
      disabled: !useClientReady() || isFetchingNextPage || isLoading || !hasNextPage,
    },
  );

  const isMobile = useMediaQuery({ maxWidth: below(widthBreakpoints.px576) });

  useEffect(() => {
    data && setSpeakerToShow(flatten(data.pages.map((n) => n.result)));
  }, [data]);

  return (
    <Layout
      widget={
        !isEmpty(speakersToShow) &&
        speakersToShow.length % listSpeakersPageSize === 0 && <SpeakerListingYoutubeStickyButton />
      }
      leftPanel={<SpeakerListingLeftPanel />}
      title="Nasi wykładowcy"
    >
      <LayoutBox>
        <ListingHeader>
          <ListingTitle>
            Nasi wykładowcy{' '}
            {data?.pages && data.pages.length > 0 && <span>({data?.pages?.[0].totalResult})</span>}
          </ListingTitle>
          <Description>
            Nasza kadra wykładowcza to mocna strona PFP. Znajdziesz tu praktyków, chętnych do
            dzielenia się swoją wiedzą i doświadczeniem.
          </Description>
        </ListingHeader>

        {!blank && (
          <StyledListingResults>
            {isLoading
              ? range(0, 3).map((item, index) => (
                  <Card key={index}>
                    <SpeakerCardSkeleton />
                  </Card>
                ))
              : speakersToShow.map((speaker) => {
                  return (
                    <ListingItem key={speaker.id}>
                      <SpeakerCard mobileImageBig={isMobile} speaker={speaker} />
                    </ListingItem>
                  );
                })}
            <SpeakerListingYoutubeCard
              mobileOnly={speakersToShow.length % listSpeakersPageSize === 0}
            />
          </StyledListingResults>
        )}
        {!isFetchingNextPage && !isLoading && hasNextPage && <div ref={moreEventsTriggerRef} />}
        {(isLoading || isFetchingNextPage) && <Loader />}
      </LayoutBox>
    </Layout>
  );
};

export const getStaticProps = VendorGetStaticProps<SpeakersPageProps>(async () => {
  const speakers = await getSpeakers({
    pageParam: 1,
  });

  return {
    props: {
      speakersPage: speakers,
    },
  };
});

export default SpeakersPage;
